import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { parseAnnotationToCalibration } from '../utils/annotationToCalibration';
import { CalibrationConfig } from '../types';

interface EdgeGuidedDrawModeProps {
  videoRef: React.RefObject<HTMLVideoElement>;
  canvasRef: React.RefObject<HTMLCanvasElement>;
  overlayContainerRef: React.RefObject<HTMLDivElement>;
  onApplyCalibration?: (config: CalibrationConfig) => void;
}

// Wall colors for drawing
const WALL_COLORS = {
  'start': '#00BCD4',    // Cyan
  '10pt': '#8BC34A',     // Light Green
  '20pt': '#CDDC39',     // Lime
  '30pt': '#4CAF50',     // Green
  '40pt': '#2196F3',     // Blue
  '50pt': '#FFC107',     // Yellow
  '100pt': '#f44336',    // Red
  'gutter': '#9C27B0',   // Purple
  'custom': '#FF9800'    // Orange
} as const;

type WallType = keyof typeof WALL_COLORS;

export function EdgeGuidedDrawMode({ videoRef, canvasRef, overlayContainerRef, onApplyCalibration }: EdgeGuidedDrawModeProps) {
  // Drawing state
  const [selectedWallType, setSelectedWallType] = useState<WallType | null>(null);
  const [brushSize, setBrushSize] = useState(3);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingEnabled, setDrawingEnabled] = useState(true);
  
  // Edge detection parameters
  const [sobelThreshold, setSobelThreshold] = useState(50);
  const [edgeOpacity, setEdgeOpacity] = useState(0.5);
  const [showEdges, setShowEdges] = useState(true);
  
  // Canvas refs
  const drawingCanvasRef = useRef<HTMLCanvasElement>(null);
  const edgeCanvasRef = useRef<HTMLCanvasElement>(null);
  const [lastPos, setLastPos] = useState<{ x: number; y: number } | null>(null);
  const [canvasInitialized, setCanvasInitialized] = useState(false);

  // Callback ref for drawing canvas initialization
  const setDrawingCanvasRef = useCallback((canvas: HTMLCanvasElement | null) => {
    (drawingCanvasRef as React.MutableRefObject<HTMLCanvasElement | null>).current = canvas;
    
    if (canvas && videoRef.current) {
      const video = videoRef.current;
      
      const initCanvas = () => {
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          setCanvasInitialized(true);
          console.log('✅ Drawing canvas initialized:', {
            width: canvas.width,
            height: canvas.height
          });
        }
      };
      
      initCanvas();
      video.addEventListener('loadedmetadata', initCanvas);
      video.addEventListener('playing', initCanvas);
    }
  }, [videoRef]);

  // Edge detection processing loop
  useEffect(() => {
    if (!videoRef.current || !edgeCanvasRef.current || !showEdges) return;
    
    const processFrame = () => {
      const video = videoRef.current;
      const edgeCanvas = edgeCanvasRef.current;
      
      if (!video || !edgeCanvas || video.readyState < 2) {
        requestAnimationFrame(processFrame);
        return;
      }
      
      const ctx = edgeCanvas.getContext('2d');
      if (!ctx) return;
      
      // Set canvas size to match video
      if (edgeCanvas.width !== video.videoWidth || edgeCanvas.height !== video.videoHeight) {
        edgeCanvas.width = video.videoWidth;
        edgeCanvas.height = video.videoHeight;
      }
      
      // Draw video frame
      ctx.drawImage(video, 0, 0);
      
      // Get image data
      const imageData = ctx.getImageData(0, 0, edgeCanvas.width, edgeCanvas.height);
      
      // Apply edge detection (simplified)
      const edges = detectEdgesSimple(imageData, sobelThreshold);
      
      // Display edges with opacity
      ctx.globalAlpha = edgeOpacity;
      ctx.putImageData(edges, 0, 0);
      ctx.globalAlpha = 1.0;
      
      requestAnimationFrame(processFrame);
    };
    
    const animationId = requestAnimationFrame(processFrame);
    
    return () => cancelAnimationFrame(animationId);
  }, [videoRef, showEdges, sobelThreshold, edgeOpacity]);

  // Simplified edge detection for real-time performance
  function detectEdgesSimple(imageData: ImageData, threshold: number): ImageData {
    const width = imageData.width;
    const height = imageData.height;
    const data = imageData.data;
    const output = new ImageData(width, height);
    
    // Convert to grayscale and apply Sobel operator
    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        const idx = (y * width + x) * 4;
        
        // Grayscale
        const gray = (data[idx] + data[idx + 1] + data[idx + 2]) / 3;
        
        // Sobel X and Y
        const gx = (
          -1 * data[((y - 1) * width + (x - 1)) * 4] +
          1 * data[((y - 1) * width + (x + 1)) * 4] +
          -2 * data[(y * width + (x - 1)) * 4] +
          2 * data[(y * width + (x + 1)) * 4] +
          -1 * data[((y + 1) * width + (x - 1)) * 4] +
          1 * data[((y + 1) * width + (x + 1)) * 4]
        ) / 3;
        
        const gy = (
          -1 * data[((y - 1) * width + (x - 1)) * 4] +
          -2 * data[((y - 1) * width + x) * 4] +
          -1 * data[((y - 1) * width + (x + 1)) * 4] +
          1 * data[((y + 1) * width + (x - 1)) * 4] +
          2 * data[((y + 1) * width + x) * 4] +
          1 * data[((y + 1) * width + (x + 1)) * 4]
        ) / 3;

        const magnitude = Math.sqrt(gx * gx + gy * gy);

        // Apply threshold
        const edge = magnitude > threshold ? 255 : 0;

        output.data[idx] = edge;
        output.data[idx + 1] = edge;
        output.data[idx + 2] = edge;
        output.data[idx + 3] = 255;
      }
    }

    return output;
  }

  // Drawing handlers
  const getCanvasCoordinates = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const drawLine = (x0: number, y0: number, x1: number, y1: number) => {
    const canvas = drawingCanvasRef.current;
    if (!canvas || !selectedWallType) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.strokeStyle = WALL_COLORS[selectedWallType];
    ctx.lineWidth = brushSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!drawingEnabled || !selectedWallType) return;

    const coords = getCanvasCoordinates(e);
    if (!coords) return;

    setIsDrawing(true);
    setLastPos(coords);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !lastPos || !drawingEnabled) return;

    const coords = getCanvasCoordinates(e);
    if (!coords) return;

    drawLine(lastPos.x, lastPos.y, coords.x, coords.y);
    setLastPos(coords);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
    setLastPos(null);
  };

  // Clear drawing
  const clearDrawing = () => {
    const canvas = drawingCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  // Apply calibration
  const applyAsCalibration = () => {
    if (!drawingCanvasRef.current || !videoRef.current || !onApplyCalibration) return;

    try {
      const config = parseAnnotationToCalibration(
        drawingCanvasRef.current,
        videoRef.current.videoWidth,
        videoRef.current.videoHeight
      );
      onApplyCalibration(config);
      alert(`✅ Calibration applied!\n\nDetected:\n- ${config.holes.length} holes\n- Start Zone: ${config.startZone ? 'Yes' : 'No'}\n- Gutter Zone: ${config.gutterZone ? 'Yes' : 'No'}`);
    } catch (error) {
      console.error('Failed to parse annotation:', error);
      alert('❌ Failed to parse annotation.');
    }
  };

  // Export training data for ML
  const exportTrainingData = () => {
    if (!drawingCanvasRef.current || !edgeCanvasRef.current || !videoRef.current) {
      alert('❌ Canvas not ready. Please wait for video to load.');
      return;
    }

    try {
      // Capture current video frame
      const video = videoRef.current;
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = video.videoWidth;
      tempCanvas.height = video.videoHeight;
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) return;
      tempCtx.drawImage(video, 0, 0);
      const videoFrame = tempCanvas.toDataURL('image/png');

      // Get edge detection image
      const edgeImage = edgeCanvasRef.current.toDataURL('image/png');

      // Get drawing annotation
      const annotationImage = drawingCanvasRef.current.toDataURL('image/png');

      // Parse calibration config
      const config = parseAnnotationToCalibration(
        drawingCanvasRef.current,
        video.videoWidth,
        video.videoHeight
      );

      // Create training data object
      const trainingData = {
        timestamp: Date.now(),
        videoWidth: video.videoWidth,
        videoHeight: video.videoHeight,
        edgeDetectionParams: {
          sobelThreshold,
          edgeOpacity,
        },
        images: {
          videoFrame,      // Original camera frame
          edgeDetection: edgeImage,  // Edge detection result
          annotation: annotationImage, // User's drawing
        },
        calibration: config, // Parsed calibration zones
      };

      // Download as JSON file
      const blob = new Blob([JSON.stringify(trainingData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `training-data-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);

      console.log('📦 Training data exported:', trainingData);
      alert('✅ Training data exported!\n\nThis file contains:\n- Original video frame\n- Edge detection result\n- Your annotations\n- Calibration config\n- Edge detection parameters');
    } catch (error) {
      console.error('Failed to export training data:', error);
      alert('❌ Failed to export training data.');
    }
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        {/* Two-column layout for Edge Detection and Drawing Tools */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
          {/* Edge Detection Controls */}
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '15px',
            borderRadius: '8px'
          }}>
            <h3 style={{ fontSize: '14px', marginBottom: '15px', color: '#333' }}>⚡ Edge Detection</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={showEdges}
                onChange={(e) => setShowEdges(e.target.checked)}
              />
              <span style={{ fontSize: '13px', fontWeight: 'bold' }}>Show Edge Overlay</span>
            </label>
          </div>

          {showEdges && (
            <>
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>
                  Edge Sensitivity: {sobelThreshold}
                </label>
                <input
                  type="range"
                  min="10"
                  max="200"
                  value={sobelThreshold}
                  onChange={(e) => setSobelThreshold(Number(e.target.value))}
                  style={{ width: '100%' }}
                />
                <p style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>
                  Lower = more edges detected (more sensitive)
                </p>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>
                  Edge Opacity: {(edgeOpacity * 100).toFixed(0)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={edgeOpacity}
                  onChange={(e) => setEdgeOpacity(Number(e.target.value))}
                  style={{ width: '100%' }}
                />
              </div>
            </>
          )}
          </div>

          {/* Drawing Controls */}
          <div style={{
            backgroundColor: '#f5f5f5',
            padding: '15px',
            borderRadius: '8px'
          }}>
            <h3 style={{ fontSize: '14px', marginBottom: '15px', color: '#333' }}>🖊️ Drawing Tools</h3>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
              <input
                type="checkbox"
                checked={drawingEnabled}
                onChange={(e) => setDrawingEnabled(e.target.checked)}
              />
              <span style={{ fontSize: '13px', fontWeight: 'bold' }}>Enable Drawing</span>
            </label>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>
              Brush Size: {brushSize}px
            </label>
            <input
              type="range"
              min="1"
              max="20"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: 'bold' }}>
              Wall Type:
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {(Object.keys(WALL_COLORS) as WallType[]).map(wallType => (
                <button
                  key={wallType}
                  onClick={() => setSelectedWallType(wallType)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: selectedWallType === wallType ? WALL_COLORS[wallType] : '#fff',
                    color: selectedWallType === wallType ? 'white' : '#333',
                    border: `2px solid ${WALL_COLORS[wallType]}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '12px',
                    transition: 'all 0.2s'
                  }}
                >
                  {wallType}
                </button>
              ))}
            </div>
          </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
          <button
            onClick={clearDrawing}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            🗑️ Clear All
          </button>

          <button
            onClick={exportTrainingData}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            💾 Export Drawing
          </button>

          <button
            onClick={applyAsCalibration}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: '#9C27B0',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px'
            }}
          >
            🎯 Apply as Calibration
          </button>
        </div>
      </div>

      {/* Portal-rendered canvases */}
      {overlayContainerRef.current && createPortal(
        <>
          {/* Edge detection canvas (bottom layer) */}
          {showEdges && (
            <canvas
              ref={edgeCanvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 5
              }}
            />
          )}

          {/* Drawing canvas (top layer) */}
          <canvas
            ref={setDrawingCanvasRef}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: drawingEnabled ? 'auto' : 'none',
              cursor: drawingEnabled ? 'crosshair' : 'default',
              zIndex: 10
            }}
          />
        </>,
        overlayContainerRef.current
      )}
    </div>
  );
}

